import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelectionDetail, checkIsBuilder, checkIsTrendFollow } from 'shared-modules/hooks/select';
import { AUTO_SELECT_TAB_LOGIC_SETTING, AUTO_SELECT_TAB_SIMULATION } from 'shared-modules/constants';
import { ADD_TO_CART, MESSAGE_NO_RESULT, RUN_NOW } from 'shared-modules/constants/select';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { getJapaneseFormatDate } from 'shared-modules/services';
import { Button, Tabs, Spin } from '../../../../components';
import { useAddToCart, useRunNow } from '../../hooks';
import { LogicSetting } from '../LogicSetting';
import { ConfirmModal } from '../ConfirmModal';
import { Simulation } from '../Simulation';
import { ChartDescription } from '../ChartDescription';
import { Header } from './Header';
import styles from './detail.module.scss';
import { openAutoSelectWarningInfo } from '../../../../redux/actions/modalActions';

const TAB_ITEMS = [
  {
    id: AUTO_SELECT_TAB_SIMULATION,
    label: 'シミュレーション',
    children: <Simulation />,
  },
  {
    id: AUTO_SELECT_TAB_LOGIC_SETTING,
    label: 'ロジック設定',
    children: <LogicSetting />,
  },
];

export const Detail = memo(() => {
  const headerRef = useRef(null);
  const dispatch = useDispatch();
  const [headerHeight, setHeaderHeight] = useState(0);
  const [activeTab, setActiveTab] = useState(AUTO_SELECT_TAB_SIMULATION);
  const [visibleAddToCartConfirmModal, setVisibleAddToCartConfirmModal] = useState(false);
  const [visibleRunNowConfirmModal, setVisibleRunNowConfirmModal] = useState(false);
  const activeSelections = useSelector((state) => state.autoSelect.activeSelections);
  const publishedLabsList = useSelector((state) => state.labs.publishedLabs.list);

  const {
    strategyGroup,
    loadingSelections,
    loadingAddToCart,
    loadingRunNow,
    loadingDetail,
    loadingDetailLab,
    isSoldOut,
    strategyList,
  } = useSelectionDetail();

  const notSelected = strategyGroup == null;

  const handleClickAddToCart = useAddToCart();
  const isBuilder = checkIsBuilder(strategyList);
  const isShare = strategyGroup?.labId != null;
  const isTrendFollow = checkIsTrendFollow(strategyList);

  const handleClickAddToCartWithConfirm = useCallback(() => {
    if (strategyGroup == null) {
      return;
    }

    if (isShare || isBuilder || isTrendFollow) {
      setVisibleAddToCartConfirmModal(true);
    } else {
      handleClickAddToCart();
    }
  }, [handleClickAddToCart, isBuilder, isShare, isTrendFollow, strategyGroup]);

  const handleCloseAddToCartConfirmModal = useCallback(() => {
    setVisibleAddToCartConfirmModal(false);
  }, []);

  const handleClickRunNow = useRunNow();

  const handleClickRunNowWithConfirm = useCallback(() => {
    if (strategyGroup == null) {
      return;
    }

    if (isShare || isBuilder || isTrendFollow) {
      setVisibleRunNowConfirmModal(true);
    } else {
      handleClickRunNow();
    }
  }, [handleClickRunNow, isBuilder, isShare, isTrendFollow, strategyGroup]);

  const handleCloseRunNowConfirmModal = useCallback(() => {
    setVisibleRunNowConfirmModal(false);
  }, []);

  const handleOpenWarning = useCallback(
    () =>
      dispatch(
        openAutoSelectWarningInfo({
          termEndDate: getJapaneseFormatDate(strategyGroup?.simulationStats?.termEnd),
          isLabPreview: false,
        }),
      ),
    [dispatch, strategyGroup?.simulationStats?.termEnd],
  );

  useEffect(() => {
    if (!window.ResizeObserver || loadingSelections || loadingDetail || loadingDetailLab || strategyGroup == null) {
      return () => {};
    }
    const header = headerRef.current;
    const rect = headerRef.current?.getBoundingClientRect();
    setHeaderHeight(rect?.height ?? 0);
    const observer = new window.ResizeObserver((entries) => {
      const [target] = entries;
      const { height = 0 } = target?.contentRect ?? {};
      setHeaderHeight(height);
    });
    observer.observe(header);
    return () => {
      observer.unobserve(header);
    };
  }, [loadingSelections, loadingDetail, loadingDetailLab, strategyGroup]);

  useEffect(() => {
    if (loadingSelections || loadingDetail || loadingDetailLab) {
      setActiveTab(AUTO_SELECT_TAB_SIMULATION);
    }
  }, [loadingSelections, loadingDetail, loadingDetailLab]);

  const toolbar = useMemo(() => {
    if (activeTab !== AUTO_SELECT_TAB_LOGIC_SETTING) {
      return (
        <div className={styles.helpIconArea} role="button" tabIndex={0} onClick={handleOpenWarning} aria-hidden>
          <i className={classNames('material-icons-outlined', styles.help)}>help_outline</i>
        </div>
      );
    }
    return <ChartDescription className={styles.toolbar} />;
  }, [activeTab, handleOpenWarning]);

  if (
    loadingSelections ||
    loadingDetail ||
    loadingDetailLab ||
    (strategyGroup == null && (activeSelections.length || publishedLabsList.length))
  ) {
    return <Spin className={styles.loader} />;
  }

  if (strategyGroup == null) {
    return <div className={styles.empty}>{MESSAGE_NO_RESULT}</div>;
  }

  return (
    <>
      <div className={styles.container}>
        <div ref={headerRef} className={styles.header}>
          <Header strategyGroup={strategyGroup} loadingSelections={loadingSelections} />
        </div>
        <div className={styles.content} style={{ height: `calc(100% - ${headerHeight}px - 60px)` }}>
          <div className={styles.tabArea}>
            <Tabs
              tabsClassName={styles.tabs}
              items={TAB_ITEMS}
              activeKey={activeTab}
              onChange={setActiveTab}
              toolbar={toolbar}
            />
          </div>
        </div>
        <div className={styles.footer}>
          <Button
            width={160}
            onClick={handleClickAddToCartWithConfirm}
            loading={loadingAddToCart}
            disabled={notSelected || isSoldOut}
            secondary
          >
            {ADD_TO_CART}
          </Button>
          <Button
            className={classNames(styles.greenButton)}
            width={160}
            onClick={handleClickRunNowWithConfirm}
            loading={loadingRunNow}
            disabled={notSelected}
          >
            {RUN_NOW}
          </Button>
        </div>
      </div>
      {visibleAddToCartConfirmModal && (
        <ConfirmModal
          isShare={isShare}
          isBuilder={isBuilder}
          isTrendFollow={isTrendFollow}
          visible={visibleAddToCartConfirmModal}
          onClose={handleCloseAddToCartConfirmModal}
          onClick={handleClickAddToCart}
        />
      )}
      {visibleRunNowConfirmModal && (
        <ConfirmModal
          isShare={isShare}
          isBuilder={isBuilder}
          isTrendFollow={isTrendFollow}
          visible={visibleRunNowConfirmModal}
          onClose={handleCloseRunNowConfirmModal}
          onClick={handleClickRunNow}
        />
      )}
    </>
  );
});
